import { FC, useMemo, useRef, useEffect, useState } from 'react';
import { FormatLinkedText } from 'brg-longread';
import { useTranslation } from 'react-i18next';
import { FlexBox, RectangularButtonFilled, RectangularButtonHollow } from '@brg/ui';
import ResizedImageEnhanced from '../../../../components/ResizedImageEnhanced';
import {
  LongreadBlockWrapper,
  ColoredBlock as StyledColoredBlock,
  ColoredTextBlock,
  ColoredCaptionText,
  ImageBlock,
  ButtonsContainer,
} from '../../styles/LongreadBlockRenderer.styles';
import { colorObj, colorObjFonts, invertColor } from '../../utils/colorUtils';
import { Lang } from '@brg/api';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { MarksComponent } from '../MarksComponent';
import { QuoteInfoComponent } from '../QuoteInfoComponent';
import { bigFontStyles, biggestFontStyles, regularFontStyles } from '../../styles/quoteBlockFontStyles';

export const extractor = (data: any, field: string, defaultValue: unknown) => {
  if (!!data?.length) {
      const item = data?.find((f: any) => f?.name === field)?.value;
      const result = typeof item === 'string' ? JSON.parse(item) : item;
      return result || defaultValue;
  }
  return defaultValue;
}

export const MarksType = {
  type1: 'type1',
  type2: 'type2',
  type3: 'type3',
}

export const getFontSchema = (post: any) => {

  if (post?.style !== 'quote') {
    return regularFontStyles
  }

  return post?.text?.length < 80 ? biggestFontStyles : bigFontStyles

}

interface ColoredBlockProps {
  block: any;
  backgroundColor?: string;
  padding?: string;
  paddingMobile?: string;
}

interface CaptionPositionItem {
  visible?: boolean;
  text?: string;
}

interface CaptionPosition {
  top?: CaptionPositionItem;
  bottom?: CaptionPositionItem;
}

const ColoredFilledButton = styled(RectangularButtonFilled)<{ $buttonColor: string; $buttonBg: string }>`
  color: ${({ $buttonColor }) => $buttonColor} !important;
  background: ${({ $buttonBg }) => $buttonBg} !important;
  border: 1px solid ${({ $buttonBg }) => $buttonBg} !important;

  &:hover,
  &:active {
    color: ${({ $buttonColor }) => $buttonColor} !important;
    background: ${({ $buttonBg }) => $buttonBg} !important;
    border: 1px solid ${({ $buttonBg }) => $buttonBg} !important;
  }
`;

export const ColoredHollowButton = styled(RectangularButtonHollow)<{ $buttonColor: string }>`
  color: ${({ $buttonColor }) => $buttonColor} !important;
  border: 1px solid ${({ $buttonColor }) => $buttonColor} !important;
  background: transparent !important;

  &:hover,
  &:active {
    color: ${({ $buttonColor }) => $buttonColor} !important;
    border: 1px solid ${({ $buttonColor }) => $buttonColor} !important;
    background: transparent !important;
  }
`;

export const OffsetContainer = styled(FlexBox)`
  flex-flow: column;
  width: 100%;
  gap: 16px;
  max-width: 1100px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-width: 900px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    gap: 8px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: none;
    padding: 0 10px;
  }
`;

export const ColoredBlock: FC<ColoredBlockProps> = ({ block, backgroundColor, padding, paddingMobile }) => {
  const { i18n } = useTranslation();
  const hasAttachments = block.attachments && block.attachments.length > 0;
  const firstAttachment = hasAttachments ? block.attachments[0] : null;
  const files = firstAttachment?.files || [];
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<42 | 32>(42);

  const fontSchema = getFontSchema(block)

  const postColor = block.color 
    ? (block.color[0] === '#' ? block.color : (colorObj[block.color] || '#FEFEFE'))
    : (block.postColor || '#FEFEFE');
  
  const fontColor = block.backgroundImage
    ? '#151514'
    : (block.color?.[0] === '#'
      ? invertColor(block.color, '#FEFEFE', '#151514')
      : (block.color && colorObjFonts[block.color] ? colorObjFonts[block.color] : (block.fontColor || '#151514')));

  const quoteData = useMemo(() => {
    return  extractor(block?.additionalInfo, 'quoteData', null)
  } , [block])

  const captionPosition = useMemo<CaptionPosition | null>(() => {
    if (!Array.isArray(block?.additionalInfo)) return null;

    const rawValue = block.additionalInfo.find((item: any) => item?.name === 'captionPosition')?.value;
    if (!rawValue) return null;

    if (typeof rawValue === 'string') {
      try {
        return JSON.parse(rawValue);
      } catch {
        return null;
      }
    }

    return typeof rawValue === 'object' ? rawValue : null;
  }, [block?.additionalInfo]);

  const topCaption = useMemo(() => {
    const text = captionPosition?.top?.text?.trim();
    if (!captionPosition?.top?.visible || !text) return null;
    return text;
  }, [captionPosition]);

  const bottomCaption = useMemo(() => {
    const text = captionPosition?.bottom?.text?.trim();
    if (!captionPosition?.bottom?.visible || !text) return null;
    return text;
  }, [captionPosition]);

  const hasMainText = useMemo(
    () => typeof block.text === 'string' && block.text.trim().length > 0,
    [block.text],
  );

 

  const showTextColumn =
    hasMainText || topCaption || bottomCaption;

  const marksType = useMemo(() => {
    return  quoteData?.marks ?? 'type_3'
  } , [block])


  const buttons = useMemo(() => {
    if (!block.buttons || !Array.isArray(block.buttons) || block.buttons.length === 0) {
      return null;
    }

    return block.buttons.map((button: any, index: number) => {
      const buttonTitle = Lang(button.title || [], i18n.language);
      if (!buttonTitle || !button.link) return null;
      const isFilledButton = button.uprise === 1;
      const buttonTextColor = isFilledButton ? postColor : fontColor;
      const buttonBackgroundColor = isFilledButton ? fontColor : 'transparent';

      return (
        <a
          key={button.id || index}
          href={button.link}
          target={button.type === 0 ? '_blank' : '_self'}
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {isFilledButton ? (
            <ColoredFilledButton $buttonColor={buttonTextColor} $buttonBg={buttonBackgroundColor}>
              {buttonTitle}
            </ColoredFilledButton>
          ) : (
            <ColoredHollowButton $buttonColor={buttonTextColor}>
              {buttonTitle}
            </ColoredHollowButton>
          )}
        </a>
      );
    }).filter(Boolean);
  }, [block.buttons, fontColor, i18n.language, postColor]);

  useEffect(() => {
    if (!textRef.current || !containerRef.current || !hasMainText) return;

    const checkOverflow = () => {
      const textElement = textRef.current;
      const containerElement = containerRef.current;
      if (!textElement || !containerElement) return;

      const containerRect = containerElement.getBoundingClientRect();
      const containerHeight = containerRect.height - 96;
      const textHeight = textElement.scrollHeight;
      if (textHeight > containerHeight && fontSize === 42) {
        setFontSize(32);
      }
    };

    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [hasMainText, fontSize]);

  return (
    <LongreadBlockWrapper>
      <StyledColoredBlock ref={containerRef} backgroundColor={postColor} textColor={fontColor} $padding={padding} $mobilePadding={paddingMobile}>
        {showTextColumn && (
          <MarksComponent type={marksType}>
            <OffsetContainer>
              {topCaption && (
                <ColoredCaptionText textColor={fontColor} promoteToMainText={!hasMainText}>
                  <FormatLinkedText text={topCaption} />
                </ColoredCaptionText>
              )}
              {hasMainText && (
                <ColoredTextBlock fontSchema={fontSchema} ref={textRef} textColor={fontColor} fontSize={fontSize}>
                  <FormatLinkedText text={block.text} />
                </ColoredTextBlock>
              )}
              <QuoteInfoComponent post={block} />
              {bottomCaption && (
                <ColoredCaptionText textColor={fontColor}>
                  <FormatLinkedText text={bottomCaption} />
                </ColoredCaptionText>
              )}
            </OffsetContainer>
          </MarksComponent>
        )}
        {buttons && buttons.length > 0 && (
          <ButtonsContainer>
            {buttons}
          </ButtonsContainer>
        )}
        {hasAttachments && (
          <ImageBlock>
            <ResizedImageEnhanced files={files} />
          </ImageBlock>
        )}
      </StyledColoredBlock>
    </LongreadBlockWrapper>
  );
};
import { FC, useMemo, useRef, useEffect, useState } from 'react';
import { FormatLinkedText } from 'brg-longread';
import { useTranslation } from 'react-i18next';
import { FlexBox, RectangularButtonFilled, RectangularButtonHollow } from '@brg/ui';
import ResizedImageEnhanced from '../../../../components/ResizedImageEnhanced';
import {
  LongreadBlockWrapper,
  ColoredBlock as StyledColoredBlock,
  ColoredTextBlock,
  ColoredCaptionText,
  ImageBlock,
  ButtonsContainer,
} from '../../styles/LongreadBlockRenderer.styles';
import { colorObj, colorObjFonts, invertColor } from '../../utils/colorUtils';
import { Lang } from '@brg/api';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { MarksComponent } from '../MarksComponent';
import { QuoteInfoComponent } from '../QuoteInfoComponent';
import { bigFontStyles, biggestFontStyles, regularFontStyles } from '../../styles/quoteBlockFontStyles';

export const extractor = (data: any, field: string, defaultValue: unknown) => {
  if (!!data?.length) {
      const item = data?.find((f: any) => f?.name === field)?.value;
      const result = typeof item === 'string' ? JSON.parse(item) : item;
      return result || defaultValue;
  }
  return defaultValue;
}

export const MarksType = {
  type1: 'type1',
  type2: 'type2',
  type3: 'type3',
}

export const getFontSchema = (post: any) => {

  if (post?.style !== 'quote') {
    return regularFontStyles
  }

  return post?.text?.length < 80 ? biggestFontStyles : bigFontStyles

}

interface ColoredBlockProps {
  block: any;
  backgroundColor?: string;
  padding?: string;
  paddingMobile?: string;
}

interface CaptionPositionItem {
  visible?: boolean;
  text?: string;
}

interface CaptionPosition {
  top?: CaptionPositionItem;
  bottom?: CaptionPositionItem;
}

const ColoredFilledButton = styled(RectangularButtonFilled)<{ $buttonColor: string; $buttonBg: string }>`
  color: ${({ $buttonColor }) => $buttonColor} !important;
  background: ${({ $buttonBg }) => $buttonBg} !important;
  border: 1px solid ${({ $buttonBg }) => $buttonBg} !important;

  &:hover,
  &:active {
    color: ${({ $buttonColor }) => $buttonColor} !important;
    background: ${({ $buttonBg }) => $buttonBg} !important;
    border: 1px solid ${({ $buttonBg }) => $buttonBg} !important;
  }
`;

export const ColoredHollowButton = styled(RectangularButtonHollow)<{ $buttonColor: string }>`
  color: ${({ $buttonColor }) => $buttonColor} !important;
  border: 1px solid ${({ $buttonColor }) => $buttonColor} !important;
  background: transparent !important;

  &:hover,
  &:active {
    color: ${({ $buttonColor }) => $buttonColor} !important;
    border: 1px solid ${({ $buttonColor }) => $buttonColor} !important;
    background: transparent !important;
  }
`;

export const OffsetContainer = styled(FlexBox)`
  flex-flow: column;
  width: 100%;
  gap: 16px;
  max-width: 1100px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-width: 900px;
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    gap: 8px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: none;
    padding: 0 10px;
  }
`;

export const ColoredBlock: FC<ColoredBlockProps> = ({ block, backgroundColor, padding, paddingMobile }) => {
  const { i18n } = useTranslation();
  const hasAttachments = block.attachments && block.attachments.length > 0;
  const firstAttachment = hasAttachments ? block.attachments[0] : null;
  const files = firstAttachment?.files || [];
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<42 | 32>(42);

  const fontSchema = getFontSchema(block)

  const postColor = block.color 
    ? (block.color[0] === '#' ? block.color : (colorObj[block.color] || '#FEFEFE'))
    : (block.postColor || '#FEFEFE');
  
  const fontColor = block.backgroundImage
    ? '#151514'
    : (block.color?.[0] === '#'
      ? invertColor(block.color, '#FEFEFE', '#151514')
      : (block.color && colorObjFonts[block.color] ? colorObjFonts[block.color] : (block.fontColor || '#151514')));

  const quoteData = useMemo(() => {
    return  extractor(block?.additionalInfo, 'quoteData', null)
  } , [block])

  const captionPosition = useMemo<CaptionPosition | null>(() => {
    if (!Array.isArray(block?.additionalInfo)) return null;

    const rawValue = block.additionalInfo.find((item: any) => item?.name === 'captionPosition')?.value;
    if (!rawValue) return null;

    if (typeof rawValue === 'string') {
      try {
        return JSON.parse(rawValue);
      } catch {
        return null;
      }
    }

    return typeof rawValue === 'object' ? rawValue : null;
  }, [block?.additionalInfo]);

  const topCaption = useMemo(() => {
    const text = captionPosition?.top?.text?.trim();
    if (!captionPosition?.top?.visible || !text) return null;
    return text;
  }, [captionPosition]);

  const bottomCaption = useMemo(() => {
    const text = captionPosition?.bottom?.text?.trim();
    if (!captionPosition?.bottom?.visible || !text) return null;
    return text;
  }, [captionPosition]);

  const hasMainText = useMemo(
    () => typeof block.text === 'string' && block.text.trim().length > 0,
    [block.text],
  );

 

  const showTextColumn =
    hasMainText || topCaption || bottomCaption;

  const marksType = useMemo(() => {
    return  quoteData?.marks ?? 'type_3'
  } , [block])


  const buttons = useMemo(() => {
    if (!block.buttons || !Array.isArray(block.buttons) || block.buttons.length === 0) {
      return null;
    }

    return block.buttons.map((button: any, index: number) => {
      const buttonTitle = Lang(button.title || [], i18n.language);
      if (!buttonTitle || !button.link) return null;
      const isFilledButton = button.uprise === 1;
      const buttonTextColor = isFilledButton ? postColor : fontColor;
      const buttonBackgroundColor = isFilledButton ? fontColor : 'transparent';

      return (
        <a
          key={button.id || index}
          href={button.link}
          target={button.type === 0 ? '_blank' : '_self'}
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {isFilledButton ? (
            <ColoredFilledButton $buttonColor={buttonTextColor} $buttonBg={buttonBackgroundColor}>
              {buttonTitle}
            </ColoredFilledButton>
          ) : (
            <ColoredHollowButton $buttonColor={buttonTextColor}>
              {buttonTitle}
            </ColoredHollowButton>
          )}
        </a>
      );
    }).filter(Boolean);
  }, [block.buttons, fontColor, i18n.language, postColor]);

  useEffect(() => {
    if (!textRef.current || !containerRef.current || !hasMainText) return;

    const checkOverflow = () => {
      const textElement = textRef.current;
      const containerElement = containerRef.current;
      if (!textElement || !containerElement) return;

      const containerRect = containerElement.getBoundingClientRect();
      const containerHeight = containerRect.height - 96;
      const textHeight = textElement.scrollHeight;
      if (textHeight > containerHeight && fontSize === 42) {
        setFontSize(32);
      }
    };

    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [hasMainText, fontSize]);

  return (
    <LongreadBlockWrapper>
      <StyledColoredBlock ref={containerRef} backgroundColor={postColor} textColor={fontColor} $padding={padding} $mobilePadding={paddingMobile}>
        {showTextColumn && (
          <MarksComponent type={marksType}>
            <OffsetContainer>
              {topCaption && (
                <ColoredCaptionText textColor={fontColor} promoteToMainText={!hasMainText}>
                  <FormatLinkedText text={topCaption} />
                </ColoredCaptionText>
              )}
              {hasMainText && (
                <ColoredTextBlock fontSchema={fontSchema} ref={textRef} textColor={fontColor} fontSize={fontSize}>
                  <FormatLinkedText text={block.text} />
                </ColoredTextBlock>
              )}
              <QuoteInfoComponent post={block} />
              {bottomCaption && (
                <ColoredCaptionText textColor={fontColor}>
                  <FormatLinkedText text={bottomCaption} />
                </ColoredCaptionText>
              )}
            </OffsetContainer>
          </MarksComponent>
        )}
        {buttons && buttons.length > 0 && (
          <ButtonsContainer>
            {buttons}
          </ButtonsContainer>
        )}
        {hasAttachments && (
          <ImageBlock>
            <ResizedImageEnhanced files={files} />
          </ImageBlock>
        )}
      </StyledColoredBlock>
    </LongreadBlockWrapper>
  );
};
